import { ref } from 'vue';
import { useHead } from 'nuxt/app';
import axios from 'axios';
import useAuthStore from '~/stores/auth';

// Shared state - defined outside the composable function
// This ensures all components using this composable share the same reactive state
const showRegisterModal = ref(false);
const registerFormLoading = ref(false);

// Contributor contact modal state
const showContributorContactModal = ref(false);
const contributorFormData = ref({ contributorId: '', contributor: '', pageUrl: '', portal: '', imageUrl: '' });

// Contributor contact form fields
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const comments = ref('');
const submitting = ref(false);
const submitted = ref(false);
const submitError = ref('');
const fieldErrors = ref<Record<string, string[]>>({});

export const useContactModals = () => {
  // Load Brevo scripts - only needs to be done once per page
  useHead({
    link: [
      {
        rel: 'stylesheet',
        href: 'https://meetings.brevo.com/assets/styles/popup.css',
        media: 'print',
        onload: "this.media='all'"
      }
    ],
    script: [
      {
        src: 'https://meetings.brevo.com/assets/libs/popup.min.js',
        type: 'text/javascript',
        defer: true
      }
    ]
  });

  const openRegisterModal = () => {
    registerFormLoading.value = true;
    showRegisterModal.value = true;
  };

  const closeRegisterModal = () => {
    showRegisterModal.value = false;
  };

  const onRegisterFormLoad = () => {
    registerFormLoading.value = false;
  };

  const openBookCallModal = () => {
    if (typeof window !== 'undefined' && (window as any).BrevoBookingPage) {
      (window as any).BrevoBookingPage.initStaticButton({
        url: 'https://meet.brevo.com/info-ipa/borderless'
      });
    }
  };

  const openContributorContactModal = (contributorId: string, contributorName: string, contributorImageUrl: string = '') => {
    const portal = typeof window !== 'undefined' ? localStorage.getItem('portal') || '' : '';

    contributorFormData.value = {
      contributorId,
      contributor: contributorName,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      portal,
      imageUrl: contributorImageUrl
    };
    resetContributorContactForm();
    showContributorContactModal.value = true;
  };

  const closeContributorContactModal = () => {
    showContributorContactModal.value = false;
  };

  const resetContributorContactForm = () => {
    firstName.value = '';
    lastName.value = '';
    email.value = '';
    comments.value = '';
    submitting.value = false;
    submitted.value = false;
    submitError.value = '';
    fieldErrors.value = {};
  };

  const submitContributorContactForm = async () => {
    submitting.value = true;
    submitError.value = '';
    fieldErrors.value = {};

    const authStore = useAuthStore();

    try {
      await axios.post(
        `${authStore.lmsApiBaseurl}contributor-contact-requests`,
        {
          first_name: firstName.value,
          last_name: lastName.value,
          email: email.value,
          comments: comments.value,
          contributor_id: Number(contributorFormData.value.contributorId),
          portal: contributorFormData.value.portal,
          submission_url: contributorFormData.value.pageUrl,
          email_address_check: '',
        },
        { headers: authStore.authHeaders },
      );
      submitted.value = true;
    } catch (error: any) {
      if (error.response?.status === 422 && error.response?.data?.errors) {
        fieldErrors.value = error.response.data.errors;
        return;
      }
      submitError.value = error.response?.data?.message || 'Something went wrong. Please try again.';
    } finally {
      submitting.value = false;
    }
  };

  return {
    showRegisterModal,
    registerFormLoading,
    openRegisterModal,
    closeRegisterModal,
    onRegisterFormLoad,
    openBookCallModal,
    showContributorContactModal,
    contributorFormData,
    openContributorContactModal,
    closeContributorContactModal,
    firstName,
    lastName,
    email,
    comments,
    submitting,
    submitted,
    submitError,
    fieldErrors,
    submitContributorContactForm,
    resetContributorContactForm
  };
};
